import { Button, Paper, Typography } from '@material-ui/core'
import React from 'react'

import { sentryLogger } from 'sentry'

/**
 * A crash on ONE apartment page must not take Alfred down.
 *
 * The app's only ErrorBoundary wraps <RouteHandler/> in App.js — i.e. the entire
 * router. So any render error anywhere replaced the whole UI, navigation
 * included, with "Some Error Occurred!". A ReferenceError on the Overview page
 * therefore looked like the Cluster Dashboard was broken too: the app was dead,
 * not the page.
 *
 * This boundary sits INSIDE the layout, so the sidebar and the other tabs keep
 * working and the admin can navigate away from a broken page instead of
 * reloading the app.
 *
 * `resetKey` clears the error when it changes — pass the selected apartment or
 * the route, so switching building or tab retries rather than staying stuck on
 * the fallback forever.
 */
class ApartmentErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      // Navigating away from the broken state must actually clear it.
      this.setState({ error: null })
    }
  }

  componentDidCatch(error, errorInfo) {
    // Goes to Sentry, not just the console — a page that silently white-screens
    // in production is exactly what we are trying to stop.
    sentryLogger(error, 'ApartmentErrorBoundary')
    // eslint-disable-next-line no-console
    console.error('Apartment page crashed:', error, errorInfo)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <Paper variant="outlined" style={{ margin: 24, padding: 24 }}>
        <Typography variant="h6" gutterBottom>
          This page hit an error
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          The rest of Alfred still works — use the menu to go elsewhere. The
          error has been reported.
        </Typography>
        <Typography
          variant="caption"
          component="pre"
          style={{
            whiteSpace: 'pre-wrap',
            background: '#f5f5f5',
            padding: 12,
            marginTop: 12,
            borderRadius: 4
          }}
        >
          {String(error?.message || error)}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          style={{ marginTop: 12 }}
          onClick={() => this.setState({ error: null })}
        >
          Try again
        </Button>
      </Paper>
    )
  }
}

export default ApartmentErrorBoundary
